import type { ActionType } from 'typesafe-actions';
import { all, call, delay, fork, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { uniq } from 'ramda';
import { Base64 } from 'js-base64';
import { addHours } from 'date-fns';

import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import {
  deleteDecisionSignById,
  fetchDecisionById,
  fetchDecisionCreate,
  fetchDecisionDelete,
  fetchDecisionDraft,
  fetchDecisionDraftDelete,
  fetchDecisionDraftSave,
  fetchDecisionHistory,
  fetchDecisionPrescribeCancel,
  fetchDecisionRestore,
  fetchDecisionRestoreLatestSignedVersion,
  fetchDecisionSignById,
  fetchDecisionSignedHistory,
  fetchDecisionUpdate,
  fetchDecisionXmlDownloadById,
  fetchIsDecisionCanBeSigned,
  fetchTestDecisionSignById,
} from 'APIPackage/DecisionModule/services/decisionApi';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { NOT_FOUND_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import {
  CHECKING_IN_REGESTRY_ORDER,
  CHECKING_IT_ACCREDITATION,
  CHECK_DECISION_BEFORE_SIGN_ERROR,
  CHECK_DECISION_BEFORE_SIGN_WARNING,
  CHECK_DECISION_BEFORE_SIGN_NOT_SAVED_WARNING,
  CRIPTOPRO_NOT_EXISTS_ERROR,
  DECISION_NOT_FOUND_ERROR,
  DECISION_RESTORE_SUCCESS,
  DECISION_SAVE_ERROR_WARNING,
  DECISION_SAVE_SUCCESS,
  DECISION_SIGN_ADD_SUCCESS,
  DECISION_SIGN_ERROR,
  INTERNAL_SERVER_ERROR,
  KNM_SIGN_DELETE_SUCCESS,
  PRESCRIBE_CANCEL_ERROR,
  PRESCRIBE_CANCEL_SUCCESS,
} from 'CorePackage/MainModule/constants/notices';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import { ROUTE_DECISION } from 'CorePackage/MainModule/routes/routes';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import {
  dictionariesApplicationConditionSubjectsItemGet,
  dictionariesItemValueGetSuccess,
  dictionariesKnoOrganizationsItemGet,
  dictionariesRiskIndicatorsItemGet,
  dictionariesTargetEntityFounderItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { IKnmHistoryItem } from 'APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { getInitialObject } from 'KnmErknmPackage/MainModule/common/getInitialObject';
import { getDictionariesItemValue } from 'CorePackage/DictionariesModule/common/getDictionariesItemValue';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmDictionary } from 'APIPackage/KnmModule/interfaces/IKnmDictionary';
import { convertKnmUrlIdToId } from 'KnmPackage/MainModule/common/convertKnmUrlIdToId';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { convertKnmDataViolatedRequirementsToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataViolatedRequirementsToFormValues';
import { createKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmDocumentTypeSelectOption';
import { appealStatusSelectOptions } from 'KnmErknmPackage/MainModule/constants/appealStatusSelectOptions';
import { init, subscribe } from 'KnmPackage/MainModule/common/cryptoHelpers';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { KnmHistoryOperationsEnum } from 'APIPackage/KnmModule/enums/KnmHistoryOperationsEnum';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { knmHistoryFieldOperationsMap } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldOperationsMap';
import { KNM_HISTORY_FIELD_TEXT_PREFIX } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldTextPrefix';
import { knmHistoryFields } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFields';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { doubleIdRegExp } from 'KnmPackage/MainModule/constants/doubleIdRegExp';
import { uuidRegExp } from 'KnmPackage/MainModule/constants/uuidRegExp';
import { convertDecisionDataJudgmentsToFormValues } from 'DecisionPackage/DecisionJudgmentModule/common/convertDecisionDataJudgmentsToFormValues';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import {
  getDistrictSelectOptions,
  getUavTypeSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { getUserProfileIsTest } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  knmAddVisibleModal,
  knmInfoGetEpguNotificationList,
  knmSetAccessNoticeErpId,
  knmSetAccessNoticeKnoOrganizationName,
} from 'KnmPackage/MainModule/actions/knmActions';
import { createKnmOrderSelectOption } from 'KnmPackage/MainModule/common/createKnmOrderSelectOption';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { IKnmOrderSelectOption } from 'KnmPackage/MainModule/interfaces/IKnmOrderSelectOption';
import { IKnmOrder } from 'APIPackage/KnmModule/interfaces/IKnmOrder';
import { fetchKnm, fetchKnmAgreementTemplateDownload, fetchOrders } from 'APIPackage/KnmModule/services/knmApi';
import { IReasonRequirements } from 'APIPackage/DictionariesModule/interfaces/IReasonRequirements';
import { IDictionaryVersion } from 'APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { fetchDictionaryValue } from 'APIPackage/DictionariesModule/services/dictionariesApi';
import {
  setDirtyActGuid,
  setDirtyResultDecisionGuid,
  setInitHash,
} from 'CorePackage/HeaderModule/actions/headerActions';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IDecisionOrganization } from 'APIPackage/DecisionModule/interfaces/IDecisionOrganization';
import { AUTOSAVE_DRAFT_VERSION } from 'KnmPackage/MainModule/constants/autosaveDraftVersion';
import { convertAgreementsDataToFormValues } from 'KnmErknmPackage/MainModule/common/convertAgreementsDataToFormValues';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { convertAgreementsFormValuesToData } from 'KnmErknmPackage/MainModule/common/convertAgreementsFormValuesToData';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getProsecutorOrganization } from 'functions/getProsecutorOrganization';
import { IProsecutorOffice } from 'APIPackage/KnmModule/interfaces/IProsecutorOffice';
import {
  createDictionaryWaitChannel,
  dictionaryHandlerSaga,
  globalDictionaryTracker,
} from 'functions/dictionaryHandlerSaga';
import { getInitHash } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { scrollToBlock } from 'CorePackage/MainModule/common/scrollToBlock';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { AdminActionsEnum } from 'PersonalAccountPackage/AdminModule/enums/AdminActionsEnum';

import { watchDecisionDictionariesSaga } from './decisionDictionariesSaga';
import { IDecisionOrganizationForm } from '../interfaces/IDecisionOrganizationForm';
import {
  decisionAddVisibleModal,
  decisionAgreementTemplateDownload,
  decisionCreate,
  decisionErknmCancelPrescribe,
  decisionFillOrganizationsAndObjectsByOtherPassport,
  decisionDraftDelete,
  decisionDraftSave,
  decisionGet,
  decisionGetFailure,
  decisionGetHistoryChanges,
  decisionGetOrders,
  decisionGetRequest,
  decisionGetSignedHistoryChanges,
  decisionGetSuccess,
  decisionPatchRawData,
  decisionReload,
  decisionRemoveVisibleModal,
  decisionSaveBeforeSignSuccess,
  decisionSetHistoryChanges,
  decisionSetHistoryChangesIsLoading,
  decisionSetInitialFormValues,
  decisionSetIsCanBeSigned,
  decisionSetOrders,
  decisionSetPrescribeCancelSigned,
  decisionSetRawData,
  decisionSetSignedHistoryChanges,
  decisionSetUrlId,
  decisionSign,
  decisionTryOpenSignModal,
  decisionUpdate,
  decisionUpdateFailure,
  decisionUpdateRequest,
  decisionUpdateSuccess,
  decisionSetPrescriptionOldRules,
  decisionSetPrescriptionFailed,
} from '../actions/decisionActions';
import {
  getDecisionAOrdersSelectConditionId,
  getDecisionData,
  getDecisionDataId,
  getDecisionFormRef,
  getDecisionHistoryChangesIsOn,
  getDecisionInitialFormValues,
  getDecisionKnoSupervisionUuid,
  getDecisionOrdersSelectOptions,
  getDecisionUrlId,
} from '../selectors/decisionSelectors';
import { DecisionActionsEnum } from '../enums/DecisionActionsEnum';
import { getKindControlLabel } from '../../../KnmPackage/MainModule/common/getKindControlLabel';
import { getKindControlSelectedLabel } from '../../../KnmPackage/MainModule/common/getKindControlSelectedLabel';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getSupervisionDictionaryValue } from '../../../CorePackage/DictionariesModule/common/getSupervisionDictionaryValue';
import { IKnmErknmFormEvent } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { createInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/common/createInteractionCategorySelectOption';
import { createSubmittedDocuments } from '../../../KnmPackage/MainModule/common/createSubmittedDocuments';
import { createKnmProsecutorOrganisationSelectOption } from '../../../KnmPackage/MainModule/common/createKnmProsecutorOrganisationSelectOption';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { fetchProsecutorOrganisationsByErvkUuidAndVersion } from '../../../APIPackage/CatalogsModule/services/prosecutorOrganisationsApi';
import { createUserOrganizationWithDistrictSelectOption } from '../../../CorePackage/AccountModule/common/createUserOrganizationWithDistrictSelectOption';
import { convertInitialReasonsToFormDate } from './convertInitialReasonsToFormDate';
import { convertDecisionDataActToFormValues } from './convertDecisionDataActToFormValues';
import { convertDecisionDataResultDecisionToFormValues } from './convertDecisionDataResultDecisionToFormValues';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { IFederalDistrict } from '../../../APIPackage/KnmModule/interfaces/IFederalDistrict';
import { findRegionSelectOption } from '../../../KnmPackage/MainModule/components/regions';
import { IKnmErknmFormEventUav } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormUav';
import { IUavTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IUavTypeSelectOption';
import { ResponseError } from '../../../APIPackage/MainModule/types/ResponseError';
import { isSourceCreatedByOldRules } from '../../../CorePackage/MainModule/common/prescriptionLegacy';
import { adminGetFetchSettings } from '../../../PersonalAccountPackage/AdminModule/actions/adminAction';

export function* watchDecisionSaga() {
  yield takeEvery(DecisionActionsEnum.DECISION_CATALOGS_INIT, decisionCatalogsInitSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_GET, decisionGetSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_DOWNLOAD_XML, decisionDownloadXmlSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_AGREEMENT_TEMPLATE_DOWNLOAD, decisionDownloadAgreementTemplate);
  yield takeEvery(DecisionActionsEnum.DECISION_DELETE_SIGN, decisionDeleteSignSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_SIGN, decisionSignSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_SIGN_UPLOAD_MODAL_APPLY, decisionSignUploadModalApplySaga);
  yield takeEvery(DecisionActionsEnum.DECISION_CREATE, decisionCreateSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_UPDATE, decisionUpdateSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_DRAFT_SAVE, decisionDraftSaveSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_DRAFT_DELETE, decisionDraftDeleteSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_DELETE, decisionDeleteSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_RELOAD, decisionReloadSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_GET_HISTORY_CHANGES, decisionGetHistoryChangesSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_GET_SIGNED_HISTORY_CHANGES, decisionGetSignedHistoryChangesSaga);
  yield takeEvery(
    [DecisionActionsEnum.DECISION_GET_SUCCESS, DecisionActionsEnum.DECISION_UPDATE_SUCCESS],
    decisionInit,
  );
  yield takeEvery(DecisionActionsEnum.DECISION_RESTORE, decisionRestoreSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_TRY_OPEN_SIGN_MODAL, decisionSaveBeforeSignSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_SAVE_BEFORE_SING_SUCCESS, decisionTryOpenSignModalSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_SIGN_SUBMIT_FAIL, decisionSignSubmitFailSaga);
  yield takeEvery(
    [DecisionActionsEnum.DECISION_GET_SUCCESS, DecisionActionsEnum.DECISION_UPDATE_SUCCESS],
    getIsDecisionCanBeSignedSaga,
  );
  yield takeEvery(DecisionActionsEnum.DECISION_RESTORE_LATEST_SIGNED_VERSION, decisionRestoreLatestSignedVersionSaga);
  yield takeEvery(
    [
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS,
      DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_ALREADY_LOAD,
    ],
    decisionSetValueByOptionsSaga,
  );
  yield takeEvery(DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_SUCCESS, initDictionariesRiskIndicators);
  yield takeEvery(DecisionActionsEnum.DECISION_GET_ORDERS, decisionGetOrdersSaga);
  yield takeEvery(
    DecisionActionsEnum.DECISION_FILL_ORGANIZATIONS_AND_OBJECTS_BY_OTHER_PASSPORT,
    decisionFillOrganizationsAndObjectsByOtherPassportSaga,
  );
  yield takeEvery(DecisionActionsEnum.DECISION_SEND_AGREEMENT, decisionSendAgreementSaga);
  yield takeEvery(DecisionActionsEnum.DECISION_ERKNM_CANCEL_PRESCRIBE, decisionErknmCancelPrescribeSaga);
  yield all([watchUrlIdChangeSaga(), watchDecisionDictionariesSaga()]);
}

// TODO: Добавить список справочников для инициализации
const decisionCommonCatalogsNames: Array<CommonCatalogsNamesEnum> = [
  CommonCatalogsNamesEnum.DECISION_DOCUMENT_TYPES,
  CommonCatalogsNamesEnum.COUNTRIES,
  CommonCatalogsNamesEnum.INTERACTION_CATEGORIES,
  CommonCatalogsNamesEnum.TERMINATION_REASONS,
];

function* decisionCatalogsInitSaga() {
  yield all([
    ...decisionCommonCatalogsNames.map(catalogName => put(catalogGet(catalogName))),
    put(dictionariesKnoOrganizationsItemGet()),
  ]);
  yield put(dictionariesTargetEntityFounderItemGet());
}

function* decisionReloadSaga(action?: ActionType<typeof decisionReload>) {
  function* funcTry() {
    const callback = action?.payload.callback;
    const state: IRootState = yield select();
    const id = getDecisionDataId(state);

    if (id) {
      const data: IDecision = yield fetchDecisionById(id);

      yield put(decisionGetSuccess(data));
      yield put(setDirtyActGuid(undefined));
      yield put(setDirtyResultDecisionGuid(undefined));
      if (callback) {
        callback();
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* decisionGetSaga(action: ActionType<typeof decisionGet>) {
  const { id } = action.payload;
  const dataId: number | undefined = id ?? (yield select(getDecisionDataId));

  if (typeof dataId === 'number') {
    yield put(decisionGetRequest());
    try {
      let decisionData: IDecision | null = null;

      // Запускаем оба запроса параллельно
      const {
        draftResult,
        decisionResult,
      }: {
        draftResult: { data?: IDecision; error?: IResponseError };
        decisionResult: { data?: IDecision; error?: IResponseError };
      } = yield all({
        // Запрос черновика
        // eslint-disable-next-line func-names
        draftResult: call(function* () {
          try {
            const data: IDecision = yield call(fetchDecisionDraft, dataId);

            return { data };
          } catch (error) {
            return { error: error as IResponseError };
          }
        }),

        // Основной запрос ПР
        // eslint-disable-next-line func-names
        decisionResult: call(function* () {
          try {
            const data: IDecision = yield call(fetchDecisionById, dataId);

            return { data };
          } catch (error) {
            return { error: error as IResponseError };
          }
        }),
      });

      if (decisionResult.error) {
        throw new ResponseError(decisionResult.error);
      }

      // Если черновик получен успешно
      if (draftResult?.data) {
        // Проверяем версию черновика
        if (draftResult?.data.draftVersion === AUTOSAVE_DRAFT_VERSION) {
          decisionData = { ...(draftResult?.data || {}), draft: true };
        } else {
          // Если версии не совпадают, очищаем черновик и используем основной ресурс если он получен
          yield put(decisionDraftDelete(dataId, undefined, false));
          if (decisionResult.data) {
            decisionData = decisionResult.data;
          }
        }
      }
      // Если черновика нет, но основной запрос успешен
      else if (decisionResult?.data) {
        decisionData = decisionResult.data;
      }

      if (decisionData) {
        yield put(decisionGetSuccess(decisionData));
      }
      yield put(setDirtyActGuid(undefined));
      yield put(setDirtyResultDecisionGuid(undefined));
      if (decisionData) yield put(decisionGetSuccess(decisionData));
    } catch (err) {
      const error: IResponseError = err as IResponseError;

      yield put(decisionGetFailure());
      const isNotFoundError = error.error === NOT_FOUND_ERROR;

      if (error.status === 403) {
        if (error?.message?.includes('ACCESS_DENIED_FOR_TEST')) {
          yield put(knmAddVisibleModal(KnmModalsEnum.ACCESS_DENIED_FOR_TEST));
        } else {
          yield put(knmSetAccessNoticeErpId(error.erpId));
          yield put(knmSetAccessNoticeKnoOrganizationName(error.knoOrganizationName));
          yield put(knmAddVisibleModal(KnmModalsEnum.ACCESS_RESTRICTION_NOTICE));
        }
        // TODO: Обработка ограничения доступа
      } else if (isNotFoundError) {
        yield put(
          addNoticeAction({
            renderContent: DECISION_NOT_FOUND_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.KNM_NOT_FOUND,
          }),
        );
      }
    }
  }
}

function* decisionDownloadXmlSaga() {
  function* funcTry() {
    const id: ReturnType<typeof getDecisionDataId> = yield select(getDecisionDataId);

    if (id) {
      const response: string = yield fetchDecisionXmlDownloadById(id);
      const file = new Blob([response]);

      saveFile(file, `${id}.xml`);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DOWNLOAD_XML,
        }),
      );
    },
  });
}

function* decisionDeleteSignSaga() {
  function* funcTry() {
    const id: ReturnType<typeof getDecisionDataId> = yield select(getDecisionDataId);

    if (id) {
      yield deleteDecisionSignById(id);
      yield delay(REFRESH_DELAY);
      yield put(decisionGet());
      yield put(decisionGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: KNM_SIGN_DELETE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.DELETE_KNM_SIGN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DELETE_KNM_SIGN,
        }),
      );
    },
  });
}

function* decisionSignUploadModalApplySaga() {
  yield put(decisionRemoveVisibleModal(KnmModalsEnum.SIGN_UPLOAD_MODAL));
  yield put(decisionGet());
  yield put(
    addNoticeAction({
      renderContent: 'Подпись успешно загружена',
      noticeType: NoticeTypesEnum.SUCCESS,
      messageType: MessageTypesEnum.UPLOAD_SIGN,
    }),
  );
}

export async function decisionSignFunc(
  certificate: string,
  knmId: IKnm['id'],
  actGuid?: string,
  prescriptionGuid?: string,
  isCancelSign?: boolean,
) {
  const xmlData: string = await fetchDecisionXmlDownloadById(knmId);

  const file = new Blob([xmlData]);
  const signature: string = await subscribe(xmlData, cadesplugin.CADESCOM_CADES_BES, certificate);

  await fetchDecisionSignById({
    document: Base64.encode(xmlData),
    id: knmId,
    file,
    signature,
    fileName: `${String(knmId)}.xml`,
    actGuid,
    prescriptionGuid,
    isCancelSign,
  });
}

function* decisionSignSaga(action: ActionType<typeof decisionSign>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = action.payload.id ?? getDecisionDataId(state);
    const currentDecisionData = getDecisionData(state);
    const { certificate, actGuid, prescriptionGuid, isCancelSign } = action.payload;

    if (id) {
      yield put(decisionSetPrescribeCancelSigned(false));
      yield decisionSignFunc(certificate, id, actGuid, prescriptionGuid, isCancelSign);
      yield delay(REFRESH_DELAY);
      yield put(decisionGet());
      yield put(decisionGetHistoryChanges());
      if (actGuid || prescriptionGuid) {
        const organizationGuid = currentDecisionData?.organization?.guid || '';

        yield put(knmInfoGetEpguNotificationList(organizationGuid, 'DECISION_PASSPORT', true));
      }
      yield put(decisionSetPrescribeCancelSigned(!!isCancelSign));
      yield put(
        addNoticeAction({
          renderContent: DECISION_SIGN_ADD_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: DECISION_SIGN_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    },
  });
}

function* decisionCreateSaga(action: ActionType<typeof decisionCreate>) {
  function* funcTry() {
    const { data } = action.payload;
    const response: IDecision = yield fetchDecisionCreate(data);

    yield put(decisionSetRawData(response));

    if (response) {
      window.history.pushState(null, '', ROUTE_DECISION(response.id));
      yield put(decisionSetUrlId(response.id.toString()));

      yield put(decisionGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: DECISION_SAVE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.SAVE_KNM,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(createKnmError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: createKnmError.message,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SAVE_KNM,
        }),
      );
    },
  });
}

function* decisionUpdateSaga(action: ActionType<typeof decisionUpdate>) {
  const { data } = action.payload;

  function* funcTry() {
    yield put(decisionUpdateRequest());
    const responseData: IDecision = yield fetchDecisionUpdate(data);

    yield put(decisionUpdateSuccess(responseData));
    yield put(decisionGetHistoryChanges());
    yield put(
      addNoticeAction({
        renderContent: DECISION_SAVE_SUCCESS,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.SAVE_KNM,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      const errorMessage = error.message;

      yield put(decisionUpdateFailure());
      yield put(
        addNoticeAction({
          renderContent: errorMessage,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SAVE_KNM,
        }),
      );
    },
  });
}

function* decisionDraftSaveSaga(action: ActionType<typeof decisionDraftSave>) {
  const { data } = action.payload;

  function* funcTry() {
    const state: IRootState = yield select();
    const decisionData: IKnm = yield fetchDecisionDraftSave({ ...data, draftVersion: AUTOSAVE_DRAFT_VERSION });
    const formRef = getDecisionFormRef(state);

    if (formRef?.current) {
      formRef?.current.change('draft' as keyof IDecisionForm, decisionData.draft);
      formRef?.current.change('draftSavedAt' as keyof IDecisionForm, decisionData.draftSavedAt);
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionDraftDeleteSaga(action: ActionType<typeof decisionDraftDelete>) {
  const { id, callback, shouldReload } = action.payload;

  function* funcTry() {
    yield fetchDecisionDraftDelete(id);
    callback && callback();

    if (shouldReload) {
      yield put(decisionGet());
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionDeleteSagaFunc(ids: Array<IDecision['id']>) {
  const actions = ids.map(async (id: IDecision['id']) => {
    await fetchDecisionDelete(id);
  });

  yield Promise.all(actions);
  yield delay(REFRESH_DELAY);
  yield put(
    addNoticeAction({
      renderContent: `${ids.length > 1 ? 'Решения' : 'решение'} успешно ${ids.length > 1 ? 'удалены' : 'удалено'}.`,
      noticeType: NoticeTypesEnum.SUCCESS,
      messageType: MessageTypesEnum.DELETE_SUCCESS,
    }),
  );
}

function* decisionDeleteSaga() {
  function* funcTry() {
    const id: ReturnType<typeof getDecisionDataId> = yield select(getDecisionDataId);

    if (typeof id === 'number') {
      yield decisionDeleteSagaFunc([id]);
      yield put(decisionGet(id));
      yield put(decisionGetHistoryChanges());
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DELETE_KNM,
        }),
      );
    },
  });
}

function* decisionGetHistoryChangesSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getDecisionDataId(state);
    const isOn = getDecisionHistoryChangesIsOn(state);

    if (typeof id === 'number' && isOn) {
      yield put(decisionSetHistoryChangesIsLoading(true));
      const response: IKnmHistoryItem[] = yield fetchDecisionHistory(id);

      yield decisionFormedHistoryChanges(response);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(decisionSetHistoryChangesIsLoading(false));
    },
  });
}

function* decisionGetSignedHistoryChangesSaga(action: ActionType<typeof decisionGetSignedHistoryChanges>) {
  const { startDateTime, endDateTime } = action.payload;

  function* funcTry() {
    const state: IRootState = yield select();
    const id = getDecisionDataId(state);

    if (typeof id === 'number') {
      yield put(decisionSetHistoryChangesIsLoading(true));
      const response: IKnmHistoryItem[] = yield fetchDecisionSignedHistory(id, startDateTime, endDateTime);

      yield decisionFormedHistoryChanges(response, true);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(decisionSetHistoryChangesIsLoading(false));
    },
  });
}

function* decisionFormedHistoryChanges(decisionHistory: IKnmHistoryItem[], isSignedHistory?: boolean) {
  const newKnmHistoryChanges: IKnmHistoryItemChanges[] = [];
  const newKnmExecutionHistoryChanges: IKnmHistoryItemChanges[] = [];

  decisionHistory?.forEach(knmHistoryItem => {
    // utc в Москву через addHours(3)
    const title = `Обновлено ${knmHistoryItem.user?.fullName} (${knmHistoryItem.user?.username}) ${formatDate({
      date: addHours(new Date(knmHistoryItem.utcTimestamp), 3),
      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      parseFormat: DATE_FORMAT_ISO_FNS,
    })} (МСК)`;
    const historyChangesText: Array<string> = [];
    const executionHistoryChangesText: Array<string> = [];

    let organizationTypeTitle: unknown = '';

    knmHistoryItem.changes.forEach(changesItem => {
      const newField = changesItem.field
        .split('/')
        .map(item =>
          uuidRegExp.test(item) || (item !== '' && Number.isInteger(Number(item))) || doubleIdRegExp.test(item)
            ? '{id}'
            : item,
        )
        .join('/');

      let isAdditionalInfo = false;

      if (newField === '/objects/{id}/result/additionalInfo/{id}/information') {
        isAdditionalInfo = true;
      }
      const field = knmHistoryFields[newField];

      if (!field?.active) {
        return;
      }
      let fieldName = '';

      if (isAdditionalInfo) {
        const additionalInfoTypePath = changesItem.field.replace('information', 'resultInfoType/code');

        decisionHistory?.some(hItem => {
          const resChangesItem = hItem.changes.find(
            searchChangesItem => searchChangesItem.field === additionalInfoTypePath,
          );

          if (resChangesItem) {
            fieldName = typeof resChangesItem.newValue === 'string' ? resChangesItem.newValue : '';

            return true;
          }

          return false;
        });
      } else {
        fieldName = field.name || newField;
      }

      if (newField === '/status/name') {
        fieldName = 'Статус решения';
      }

      const { oldValue, newValue } = changesItem;
      let oldValueText = oldValue;
      let newValueText = newValue;
      let valuesText: undefined | string;
      let changeText: undefined | string;

      if (newField === '/organization/organizationType/title') {
        organizationTypeTitle = newValueText;
      }

      if (newField === '/organization/country/title') {
        if (organizationTypeTitle === 'Иностранные физические лица') {
          fieldName = 'Гражданство';
        } else {
          fieldName = 'Государство';
        }
      }

      if (field.renderFunc) {
        const {
          renderedOldValue,
          renderedNewValue,
          renderedValuesText,
          renderedChangeText,
        } = field.renderFunc(changesItem, { isDecision: true, isErp: false, isReasonsImpossibility: false });

        oldValueText = renderedOldValue;
        newValueText = renderedNewValue;
        valuesText = renderedValuesText;
        changeText = renderedChangeText;
      }

      if (valuesText === undefined) {
        valuesText = '';

        if (changesItem.operation === KnmHistoryOperationsEnum.INSERT) {
          valuesText = `"${newValueText}"`;
        } else if (changesItem.operation === KnmHistoryOperationsEnum.UPDATE) {
          valuesText = `${oldValueText ? `с "${oldValueText}" ` : ''}на "${newValueText}"`;
        }
      }

      if (changeText === undefined) {
        changeText = `${KNM_HISTORY_FIELD_TEXT_PREFIX} "${fieldName}" ${
          knmHistoryFieldOperationsMap[changesItem.operation]
        } ${valuesText}`;
      }

      if (newField === '/status/name') {
        executionHistoryChangesText.push(changeText);
      } else {
        historyChangesText.push(changeText);
      }
    });
    if (historyChangesText.length) {
      newKnmHistoryChanges.push({
        title,
        changesText: historyChangesText,
        eventDate: '',
      });
    }
    if (executionHistoryChangesText.length) {
      newKnmExecutionHistoryChanges.push({
        title,
        changesText: executionHistoryChangesText,
        eventDate: '',
      });
    }
  });

  if (isSignedHistory) {
    yield put(
      decisionSetSignedHistoryChanges({
        main: newKnmHistoryChanges,
        execution: newKnmExecutionHistoryChanges,
      }),
    );
  } else {
    yield put(
      decisionSetHistoryChanges({
        main: newKnmHistoryChanges,
        execution: newKnmExecutionHistoryChanges,
      }),
    );
  }
}

function* decisionInit(action: ActionType<typeof decisionGetSuccess>) {
  const { data } = action.payload;

  if (data) {
    const state: IRootState = yield select();
    const initHash = getInitHash(state);

    yield put(coreSetLoading(true));

    // Сбрасываем трекер справочников, чтобы «зависшие» с прошлого открытия
    // запросы не блокировали ожидание загрузки на 30 секунд
    globalDictionaryTracker.reset();

    yield fork(dictionaryHandlerSaga);
    yield decisionInitFormValuesSaga(action);
    yield decisionInitProsecutorOrganizationSaga(action);
    yield decisionInitSelectOptionsSaga(action);
    yield decisionFillAgreementProsecutorOrganizationSaga(action);
    if (initHash) {
      scrollToBlock(initHash);
      yield put(setInitHash(undefined));
    }

    yield put(coreSetLoading(false));
  }
}

function* initDictionariesRiskIndicators() {
  const state: IRootState = yield select();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    yield put(dictionariesRiskIndicatorsItemGet(knoSupervisionUuid));
  }
}

function* decisionInitSelectOptionsSaga(action: ActionType<typeof decisionGetSuccess>) {
  const { data } = action.payload;

  const { knoOrganization, kind, objects, kindControl, inspectors, organization, events, reasons, agreements } = data;
  const resultArrayActions: unknown[] = [];
  const currentDictionariesFromData: IKnmDictionary[] = [];

  if (knoOrganization) {
    yield getDictionariesItemValue(knoOrganization);
  }

  if (kind) {
    yield getDictionariesItemValue(kind);
  }

  if (kindControl) {
    if (knoOrganization) {
      yield getSupervisionDictionaryValue(knoOrganization, kindControl);
    } else {
      yield getDictionariesItemValue(kindControl);
    }
  }

  if (objects) {
    objects?.forEach(item => {
      if (item.objectType) {
        currentDictionariesFromData.push(item.objectType);
      }
      if (item.objectKind) {
        currentDictionariesFromData.push(item.objectKind);
      }
      if (item.objectSubKind) {
        currentDictionariesFromData.push(item.objectSubKind);
      }
      if (item.riskCategory) {
        currentDictionariesFromData.push(item.riskCategory);
      }
      if (item.dangerClass) {
        currentDictionariesFromData.push(item.dangerClass);
      }
    });
  }
  if (inspectors) {
    inspectors?.forEach(item => item.position && currentDictionariesFromData.push(item.position));
  }

  if (events) {
    events.forEach(event => {
      if (event.event) {
        currentDictionariesFromData.push(event.event);
      }
    });
  }

  if (organization?.decisions) {
    organization?.decisions.forEach(decision => {
      if (decision.titleSigner) {
        currentDictionariesFromData.push(decision.titleSigner);
      }
      if (decision.cancelTitleSigner) {
        currentDictionariesFromData.push(decision.cancelTitleSigner);
      }
    });
  }

  if (organization?.act?.resultContentPositionCancel) {
    currentDictionariesFromData.push(organization?.act.resultContentPositionCancel);
  }

  if (organization?.act?.titleSigner?.dictId) {
    currentDictionariesFromData.push(organization.act.titleSigner);
  }

  if (organization?.act?.knoInspectors) {
    organization.act.knoInspectors.forEach(
      inspector => inspector.position?.dictId && currentDictionariesFromData.push(inspector.position),
    );
  }

  if (agreements) {
    agreements?.forEach(item => {
      item.titleSigner && currentDictionariesFromData.push(item.titleSigner);
      item.terminate?.titleSigner?.dictId && currentDictionariesFromData.push(item.terminate?.titleSigner);
      item.execution?.titleSigner && currentDictionariesFromData.push(item.execution?.titleSigner);
      item.changes?.forEach(change => change.titleSigner && currentDictionariesFromData.push(change.titleSigner));
    });
  }

  uniq(currentDictionariesFromData).forEach(item => {
    globalDictionaryTracker.addRequest(`${item.dictId}/${item.dictVersionId}`);
    resultArrayActions.push(getDictionariesItemValue(item));
  });

  reasons?.forEach(item => {
    item.riskIndicators?.forEach(
      indicator =>
        indicator.riskIndicator && resultArrayActions.push(getDictionariesItemValue(indicator.riskIndicator)),
    );
  });

  yield all(resultArrayActions);
}

export const getOrganization = async (organization: IDecisionOrganization | null, state: IRootState) => {
  const {
    guid,
    inn,
    checkSubjectResult,
    country,
    document,
    ervkId,
    factualAddress,
    isAutocomplete,
    isSonko,
    kpp,
    locationAddress,
    mspCode,
    name,
    nkoForeignAgentsRegistryExclusionDate,
    nkoForeignAgentsRegistryInclusionDate,
    nkoForeignAgentsRegistryNumber,
    ogrn,
    organizationType,
    originalName,
    originalNameReactQuillEditor,
    rafpNza,
    snils,
    workAddress,
    smevRequestStatus,
    epguNotifications,
    isForeignSubjectAgencyNotRegisteredInRf,
    isPresentInItRegistry,
    itAccreditationResponse,
    smevItAccreditationRequestStatus,
    isNew,
    locationNotInFIAS: isLocationNotInFIAS,
    locationManualAddress,
    locationRegion,
    locationFiasRegion,
    locationRegionCodeFromFns,
  } = organization || {};

  const resultDecisions = organization?.decisions;
  const act = organization?.act;

  const organizationName = originalName || name || undefined;
  const organizationNameReactQuillEditor = handleReactQuillDeltaValue(originalNameReactQuillEditor || organizationName);
  const actForm = convertDecisionDataActToFormValues(state, act || undefined);
  const districtList = getDistrictSelectOptions(state);

  const formOrganization: IDecisionOrganizationForm = {
    guid: guid || undefined,
    inn: inn || undefined,
    ogrn: ogrn || undefined,
    originalName: organizationName,
    originalNameReactQuillEditor: organizationNameReactQuillEditor,
    name: name || undefined,
    mspCode: mspCode || undefined,
    checkSubjectResult: checkSubjectResult || null,
    isAutocomplete: typeof isAutocomplete === 'boolean' ? isAutocomplete : undefined,
    isNew: !!isNew,
    isEmpty: !organizationType && !inn && !organizationName,
    nkoForeignAgentsRegistryNumber: nkoForeignAgentsRegistryNumber || undefined,
    nkoForeignAgentsRegistryInclusionDate: nkoForeignAgentsRegistryInclusionDate || undefined,
    nkoForeignAgentsRegistryExclusionDate: nkoForeignAgentsRegistryExclusionDate || undefined,
    isNko: !!nkoForeignAgentsRegistryInclusionDate,
    kpp: kpp || undefined,
    smevRequestStatus: smevRequestStatus || undefined,
    rafpNza: rafpNza || undefined,
    country: country
      ? {
          id: country.id,
          title: country.title,
          label: country.title,
          value: country.id,
        }
      : undefined,
    organizationType: organizationType
      ? {
          id: 0,
          code: organizationType.code,
          title: organizationType.title,
          label: organizationType.title,
          value: {
            dictId: DictionaryUUIDsEnum.TARGET_ENTITY,
            dictVersionId: '',
          },
        }
      : undefined,
    isSonko: typeof isSonko === 'boolean' ? isSonko : undefined,
    locationAddress: locationAddress || undefined,
    workAddress: workAddress || undefined,
    factualAddress: factualAddress || undefined,
    snils: snils || undefined,
    document: document || undefined,
    ervkId: ervkId || undefined,
    epguNotifications: epguNotifications || undefined,
    isForeignSubjectAgencyNotRegisteredInRf:
      typeof isForeignSubjectAgencyNotRegisteredInRf === 'boolean'
        ? isForeignSubjectAgencyNotRegisteredInRf
        : undefined,
    act: actForm,
    resultDecisions: await convertDecisionDataResultDecisionToFormValues(state, resultDecisions || undefined),

    isPresentInItRegistry: isPresentInItRegistry ?? undefined,
    itAccreditationResponse: itAccreditationResponse ?? undefined,
    smevItAccreditationRequestStatus: smevItAccreditationRequestStatus ?? undefined,

    locationnotInFIAS: isLocationNotInFIAS ?? undefined,
    locationmanualAddress: locationManualAddress ?? undefined,
    locationregion: await (async () => {
      if (!isLocationNotInFIAS || !(!!locationManualAddress || !!locationAddress)) {
        return undefined;
      }

      return findRegionSelectOption(
        locationManualAddress || locationAddress || undefined,
        locationRegionCodeFromFns || locationRegion || undefined,
        districtList,
      );
    })(),
    locationfiasRegion: await (async () => {
      if (isLocationNotInFIAS || !locationAddress) {
        return undefined;
      }

      return findRegionSelectOption(
        locationAddress,
        locationRegionCodeFromFns || locationFiasRegion || undefined,
        districtList,
      );
    })(),
    locationRegionCodeFromFns: locationRegionCodeFromFns || undefined,
  };

  return formOrganization;
};

function* decisionInitFormValuesSaga(action: ActionType<typeof decisionGetSuccess>) {
  const { data } = action.payload;
  const state: IRootState = yield select();
  const initialFormValues = getDecisionInitialFormValues(state);
  let settings = getAdminFetchSettings(state);
  const uavTypeSelectOptions: IUavTypeSelectOption[] = getUavTypeSelectOptions(state);

  const {
    objects,
    kindControl,
    kind,
    basisDocument,
    prosecutorOrganization,
    district,
    organization,
    inspectors,
    violations,
    isViolations,
    additionalDocuments,
    knoOrganization,
    judgments,
    appealStatus,
    isNeedToAddAct,
    createdAt,
    events,
    isTest,
    documents,
    reasons,
    firstExtractDateTime,
    draft: isDraft,
    draftSavedAt,
    agreements,
    agreementOnApproval: isAgreementOnApproval,
    repeatPassports,
    uavTechnologies,
  } = data;

  const initialDistrictName = district?.name || null;
  const initialDistrictId = district?.id || null;

  const isUseTransferringAuthorities = isLaterThanAdminSetting(
    initialFormValues.created,
    settings,
    AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
  );

  const objectsDecision = objects?.map((object, index) => {
    const dangerClassSelectOption = object.dangerClass?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.DANGER_CLASS>(
          state,
          object.dangerClass?.dictId,
          object.dangerClass?.dictVersionId,
        )
      : undefined;
    const objectKindSelectOption = object.objectKind?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
          state,
          object.objectKind?.dictId,
          object.objectKind?.dictVersionId,
        )
      : undefined;
    const objectSubKindSelectOption = object.objectSubKind?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_SUB_KIND>(
          state,
          object.objectSubKind?.dictId,
          object.objectSubKind?.dictVersionId,
        )
      : undefined;
    const objectTypeSelectOption = object.objectType?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_TYPE>(
          state,
          object.objectType?.dictId,
          object.objectType?.dictVersionId,
        )
      : undefined;
    const riskCategorySelectOption = object.riskCategory?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_CATEGORY>(
          state,
          object.riskCategory?.dictId,
          object.riskCategory?.dictVersionId,
        )
      : undefined;

    let objRegion: IKnmDistrictSelectOption | undefined;
    let objFiasRegion: IKnmDistrictSelectOption | undefined;

    const list = getDistrictSelectOptions(state);

    // Смотрим Место проведения профилактического мероприятия
    if (object?.region) {
      if (typeof object.region === 'object') {
        objRegion = list.find(i => i.id.toString() === (object.region as IFederalDistrict)?.id?.toString());
      } else {
        objRegion = list.find(i => i.id.toString() === object?.region);
      }
    }
    if (object?.fiasRegion) {
      if (typeof object.fiasRegion === 'object') {
        objFiasRegion = list.find(i => i.id.toString() === (object.fiasRegion as IFederalDistrict)?.id?.toString());
      } else {
        objFiasRegion = list.find(i => i.id.toString() === object?.fiasRegion);
      }
    }

    return {
      dangerClass: dangerClassSelectOption || getInitialObject(initialFormValues?.objects?.[index]).dangerClass,
      objectKind: objectKindSelectOption || getInitialObject(initialFormValues?.objects?.[index]).objectKind,
      objectSubKind: objectSubKindSelectOption || getInitialObject(initialFormValues?.objects?.[index]).objectSubKind,
      objectType: objectTypeSelectOption || getInitialObject(initialFormValues?.objects?.[index]).objectType,
      riskCategory: riskCategorySelectOption || getInitialObject(initialFormValues?.objects?.[index]).riskCategory,
      address: object.address || undefined,
      guid: object.guid || undefined,
      ervkNumber: object.ervkNumber || undefined,
      ervkId: object.ervkId || undefined,
      cosId: object.cosId || undefined,
      cosVersion: object.cosVersion || undefined,
      name: object.name || undefined,
      streetGuid: object.streetGuid || undefined,
      houseGuid: object.houseGuid || undefined,
      roomGuid: object.roomGuid || undefined,
      steadGuid: object.steadGuid || undefined,
      addressFns: object.addressFns || undefined,
      autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : undefined,
      dateCreate: object.dateCreate || undefined,
      geolocation: object.geolocation || undefined,
      listId: object.guid || uuidv4(),
      isNew: object.isNew || undefined,
      manualAddress: object.manualAddress || undefined,
      notInFIAS: object.notInFIAS || undefined,
      region: objRegion,
      fiasRegion: objFiasRegion,
      regionName: object.regionName || undefined,
      isErrorFiasValue: undefined,
      inspectionResults: object.inspectionResults || undefined,
      organizationErvkId: object.organizationErvkId || undefined,
      updating: object.updating || undefined,
    };
  });

  // Проверяем поле knmErknm.reasons[0].reason.ervkUuid, чтобы загружать данные по IT-аккредитации или нет
  let reasonRequirementsValue: IReasonRequirements | undefined;
  let orderRequirementsValue: IKnmOrder | undefined;
  let isPassportByCheckPrescriptionFailed = false;
  let isPassportByCheckPrescriptionOldRules = false;

  if (reasons && reasons.length > 0) {
    const hasItOrganization = organization?.isPresentInItRegistry;
    const hasNotItOrganization = organization?.isPresentInItRegistry !== null && !organization?.isPresentInItRegistry;

    if (reasons[0].reason?.ervkUuid) {
      const dictionaryValue: IDictionaryVersion = yield fetchDictionaryValue(
        DictionaryUUIDsEnum.ERVK,
        reasons[0].reason?.ervkUuid,
      );

      reasonRequirementsValue = JSON.parse(dictionaryValue.value);

      if (reasonRequirementsValue?.it !== null) {
        if (
          (reasonRequirementsValue?.it === true && hasNotItOrganization) ||
          (reasonRequirementsValue?.it === false && hasItOrganization)
        ) {
          yield put(
            addNoticeAction({
              renderContent: CHECKING_IT_ACCREDITATION,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.CHECKING_IT_ACCREDITATION,
            }),
          );
        }
      }
    }

    if (
      reasons[0].order?.recordId &&
      reasons[0].reason?.ervkUuid &&
      knoOrganization?.dictVersionId &&
      kindControl?.dictVersionId &&
      kind?.dictVersionId
    ) {
      const orders: IKnmOrder[] = yield fetchOrders(
        knoOrganization.dictVersionId,
        kindControl.dictVersionId,
        reasons[0].reason.ervkUuid,
        false,
        kind?.dictVersionId,
        true,
      );

      orderRequirementsValue = orders.find(i => i.recordId === reasons[0].order?.recordId);

      if (orderRequirementsValue?.flagIt !== null) {
        if (
          (orderRequirementsValue?.flagIt === 'true' && hasNotItOrganization) ||
          (orderRequirementsValue?.flagIt === 'false' && hasItOrganization)
        ) {
          yield put(
            addNoticeAction({
              renderContent: CHECKING_IN_REGESTRY_ORDER,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.CHECKING_IN_REGESTRY_ORDER,
            }),
          );
        }
      }
    }

    // на холодном старте (очистка кэша) настройки могут не успеть загрузиться.
    // state захвачен в начале саги, поэтому settings из него пусты — дозагружаем и ждём.
    if (!settings?.length) {
      yield put(adminGetFetchSettings());
      yield race({
        success: take(AdminActionsEnum.ADMIN_GET_FETCH_SETTINGS_SUCCESS),
        timeout: delay(5000),
      });
      settings = yield select(getAdminFetchSettings);
    }

    if (
      reasons?.[0]?.reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS &&
      !!reasons?.[0]?.reason?.links?.[0]?.id
    ) {
      const selectedLinkData: IDecision = yield fetchDecisionById(reasons[0].reason.links[0].id);

      const isEnablePrescriptionCheck = isLaterThanAdminSetting(
        selectedLinkData.createdAt,
        settings,
        AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
      );

      if (
        isEnablePrescriptionCheck &&
        selectedLinkData.reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK
      ) {
        isPassportByCheckPrescriptionFailed = true;
      }
    }

    const firstLink = reasons[0].reason?.links?.[0];

    if (reasons[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK && !!firstLink?.id) {
      try {
        let sourceCreated: string | null;

        if (firstLink.knmType === 'Decision') {
          const sourceDecision: IDecision = yield fetchDecisionById(firstLink.id);

          sourceCreated = sourceDecision.createdAt || null;
        } else {
          const sourceKnm: IKnm = yield fetchKnm(firstLink.id);

          sourceCreated = sourceKnm.created || null;
        }

        isPassportByCheckPrescriptionOldRules = isSourceCreatedByOldRules(sourceCreated, settings);
      } catch (e) {
        isPassportByCheckPrescriptionOldRules = false;
      }
    }
  }

  yield put(decisionSetPrescriptionOldRules(isPassportByCheckPrescriptionOldRules));
  yield put(decisionSetPrescriptionFailed(isPassportByCheckPrescriptionFailed));

  if (reasons && reasons.length > 0) {
    if (reasons[0].reason?.ervkUuid && !reasons[0].order?.recordVersionId) {
      yield put(dictionariesApplicationConditionSubjectsItemGet(reasons[0].reason?.ervkUuid));
    } else if (!reasons[0].reason?.ervkUuid && reasons[0].order?.recordVersionId) {
      yield put(dictionariesApplicationConditionSubjectsItemGet(undefined, reasons[0].order?.recordVersionId));
    } else if (reasons[0].order?.recordVersionId && reasons[0].reason?.ervkUuid) {
      yield put(
        dictionariesApplicationConditionSubjectsItemGet(reasons[0].reason?.ervkUuid, reasons[0].order?.recordVersionId),
      );
    }
  }

  const kindControlSelectOption = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUPERVISION_TYPE>(
    state,
    kindControl?.dictId,
    kindControl?.dictVersionId,
  );

  const kindKnmSelectOption = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNM_KIND>(
    state,
    kind?.dictId,
    kind?.dictVersionId,
  );

  const knoOrganizationDecision = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNO_ORGANIZATIONS>(
    state,
    knoOrganization?.dictId,
    knoOrganization?.dictVersionId,
  );

  const organizationValue = organization
    ? ((yield call(getOrganization, organization, state)) as IDecisionOrganizationForm)
    : undefined;

  yield put(
    decisionSetInitialFormValues({
      draft: isDraft || undefined,
      draftSavedAt: draftSavedAt || undefined,
      created: createdAt || undefined,
      appealStatus: appealStatusSelectOptions.find(item => item.value === appealStatus),
      knoOrganization: knoOrganizationDecision,
      objects: objectsDecision,
      kindControl: kindControlSelectOption
        ? {
            ...kindControlSelectOption,
            label:
              getKindControlSelectedLabel(kindControlSelectOption, isUseTransferringAuthorities) ||
              getKindControlLabel(kindControlSelectOption, isUseTransferringAuthorities),
            selectedLabel: getKindControlSelectedLabel(kindControlSelectOption, isUseTransferringAuthorities),
          }
        : undefined,
      kind: kindKnmSelectOption,
      basisDocumentDateTime: convertInitialKnmDateToFormDate(
        basisDocument?.dateTime,
        DATE_FORMAT_LONG_MINUTE_FNS,
        DATE_FORMAT_ISO_FNS,
      ),
      basisDocumentNumber: basisDocument?.number,
      prosecutorOrganization:
        prosecutorOrganization?.id && !prosecutorOrganization?.ervkUuid && prosecutorOrganization?.name
          ? createKnmProsecutorOrganisationSelectOption({
              prosecutorOrganization: {
                ...prosecutorOrganization,
                id: prosecutorOrganization?.id,
                name: prosecutorOrganization?.name,
              },
              districtName: initialDistrictName,
              districtId: initialDistrictId?.toString() || null,
            })
          : undefined,
      organization: organizationValue,
      isOrganizationNew: !!organizationValue?.isNew,
      inspectors: inspectors?.map(item => {
        const selectOption = item.position?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
              state,
              item.position?.dictId,
              item.position?.dictVersionId,
            )
          : undefined;
        const formInspector: IKnmErknmFormInspector = {
          ...item,
          position: selectOption,
          listId: item.guid || uuidv4(),
        };

        return formInspector;
      }),
      isViolatedMandatoryRequirements: booleanSelectOptions.find(item => item.value === isViolations),
      isNeedToAddAct: booleanSelectOptions.find(item => item.value === isNeedToAddAct),
      violatedMandatoryRequirements: violations
        ? convertKnmDataViolatedRequirementsToFormValues(violations)
        : undefined,
      additionalDocuments: additionalDocuments?.map(document => ({
        ...document,
        type: document?.type ? createKnmDocumentTypeSelectOption(document?.type) : undefined,
        attachments: document.attachments || undefined,
        listId: document.guid || uuidv4(),
      })),
      documents: documents?.map(document => ({
        ...document,
        type: document?.type ? createKnmDocumentTypeSelectOption(document?.type) : undefined,
        attachments: document.attachments || undefined,
        listId: document.guid || uuidv4(),
      })),
      events: events?.map(item => {
        const selectOption = item.event?.dictId
          ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
              state,
              item.event?.dictId,
              item.event?.dictVersionId,
            )
          : undefined;

        const formEvent: IKnmErknmFormEvent = {
          type: selectOption && {
            ...selectOption,
            inspectionResult: item.event?.inspectionResult,
          },
          guid: item.guid,
          startDate: convertInitialKnmDateToFormDate(item.startDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          stopDate: convertInitialKnmDateToFormDate(item.stopDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          dateCreate: item.dateCreate,
          listId: item?.guid || uuidv4(),
          interactionCategory: item?.interactionCategory
            ? createInteractionCategorySelectOption(item.interactionCategory)
            : undefined,
          isNew: !!item.isNew,
          submittedDocuments: createSubmittedDocuments(item.submittedDocuments),
        };

        return formEvent;
      }),
      judgments: judgments ? convertDecisionDataJudgmentsToFormValues(state, judgments, organizationValue) : undefined,
      isTest: isTest ?? undefined,
      reasonRequirements: reasonRequirementsValue,
      orderRequirements: orderRequirementsValue,
      reasons: convertInitialReasonsToFormDate({ reasons, state, initialFormValues }),
      firstExtractDateTime:
        convertInitialKnmDateToFormDate(firstExtractDateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) || null,
      agreements: convertAgreementsDataToFormValues(agreements, state, undefined, undefined, organizationValue),
      agreementOnApproval: isAgreementOnApproval || undefined,
      repeatPassports: repeatPassports || undefined,
      isUavUsed: (uavTechnologies && uavTechnologies?.length > 0) || false,
      uavTechnologies: uavTechnologies?.map(x => {
        const eventUavForm: IKnmErknmFormEventUav = {
          guid: x.guid || uuidv4(),
          isNew: false,
          listId: uuidv4(),
          name: x.name || undefined,
          type: uavTypeSelectOptions.find(uavType => uavType.id === x.typeId) || undefined,
        };

        return eventUavForm;
      }),
    }),
  );
}

function* handleUrlIdChangeSaga(prevUrlId: string | undefined, nextUrlId: string | undefined) {
  const nextId = convertKnmUrlIdToId(nextUrlId);

  if (typeof nextId === 'number') {
    yield put(decisionGet(nextId));
  }
}

function* watchUrlIdChangeSaga() {
  let prevUrlId: string | undefined;

  while (true) {
    yield take(DecisionActionsEnum.DECISION_SET_URL_ID);
    const nextUrlId: string | undefined = yield select(getDecisionUrlId);

    if (nextUrlId !== prevUrlId) {
      yield handleUrlIdChangeSaga(prevUrlId, nextUrlId);
    }
    prevUrlId = nextUrlId;
  }
}

function* decisionRestoreSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getDecisionDataId(state);

    if (id) {
      yield fetchDecisionRestore(id);
      yield delay(REFRESH_DELAY);
      yield put(decisionGet(id));
      yield put(decisionGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: DECISION_RESTORE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.RESTORE_KNM,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.RESTORE_KNM,
        }),
      );
    },
  });
}

export async function decisionTestSignFunc(decisionId: IKnm['id']) {
  await fetchTestDecisionSignById(decisionId);
}

function* decisionRestoreLatestSignedVersionSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getDecisionDataId(state);

    if (id) {
      yield fetchDecisionRestoreLatestSignedVersion(id);
      yield decisionGetSaga({ type: DecisionActionsEnum.DECISION_GET, payload: { id } });
      yield decisionGetHistoryChangesSaga();
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.RESTORE_KNM,
        }),
      );
    },
  });
}

function* decisionSaveBeforeSignSaga(action: ActionType<typeof decisionTryOpenSignModal>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const formRef = getDecisionFormRef(state);

    if (formRef?.current) {
      yield call(formRef.current.submit);
      yield put(
        decisionSaveBeforeSignSuccess(
          action.payload.actGuid,
          action.payload.prescriptionGuid,
          action.payload.isNotNeedSubmitForm,
          action.payload.onClickButtonSignBeforeInnerAction,
          action.payload.abortCheckCertificate,
        ),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SAVE_BEFORE_SIGN,
        }),
      );
    },
  });
}

function* decisionTryOpenSignModalSaga(action: ActionType<typeof decisionTryOpenSignModal>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const formRef = getDecisionFormRef(state);
    const isUserTest = getUserProfileIsTest(state);
    const { actGuid, prescriptionGuid, onClickButtonSignBeforeInnerAction, abortCheckCertificate } = action.payload;

    yield init();
    yield put(
      addNoticeAction({
        renderContent: CHECK_DECISION_BEFORE_SIGN_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN,
      }),
    );
    if (formRef?.current) {
      const { dirty: isDirty, hasValidationErrors: isValidationErrors } = formRef.current.getState();

      if (isValidationErrors) {
        yield put(
          addNoticeAction({
            renderContent: CHECK_DECISION_BEFORE_SIGN_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN,
          }),
        );
      } else if (isDirty) {
        yield put(
          addNoticeAction({
            renderContent: CHECK_DECISION_BEFORE_SIGN_NOT_SAVED_WARNING,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN_NOT_SAVED,
          }),
        );
      } else if (!isUserTest) {
        yield put(
          decisionAddVisibleModal(KnmModalsEnum.SIGN_MODAL, {
            actGuid,
            prescriptionGuid,
            onClickButtonSignBeforeInnerAction,
            abortCheckCertificate,
          }),
        );
      } else {
        const id = getDecisionDataId(state);

        if (id) {
          yield delay(REFRESH_DELAY);
          yield call(decisionTestSignFunc, id);
          yield delay(REFRESH_DELAY);
          yield put(decisionGet());
          yield put(decisionGetHistoryChanges());
          yield put(
            addNoticeAction({
              renderContent: DECISION_SIGN_ADD_SUCCESS,
              noticeType: NoticeTypesEnum.SUCCESS,
              messageType: MessageTypesEnum.PASSPORT_SIGN,
            }),
          );
        }
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: CRIPTOPRO_NOT_EXISTS_ERROR,
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.CRIPTOPRO_NOT_EXISTS,
        }),
      );
    },
  });
}

function* decisionSignSubmitFailSaga() {
  yield put(
    addNoticeAction({
      renderContent: DECISION_SAVE_ERROR_WARNING,
      noticeType: NoticeTypesEnum.WARNING,
      messageType: MessageTypesEnum.SAVE_KNM,
    }),
  );
}

function* getIsDecisionCanBeSignedSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getDecisionDataId(state);

    if (typeof id === 'number') {
      const isCanBeSigned: boolean = yield fetchIsDecisionCanBeSigned(id);

      yield put(decisionSetIsCanBeSigned(isCanBeSigned));
    } else {
      yield put(decisionSetIsCanBeSigned(false));
    }
  }

  function* funcCatch() {
    yield put(decisionSetIsCanBeSigned(false));
    yield put(
      addNoticeAction({
        renderContent: INTERNAL_SERVER_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.INTERNAL_SERVER,
      }),
    );
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* decisionInitProsecutorOrganizationSaga(action: ActionType<typeof decisionGetSuccess>) {
  function* funcTry() {
    const { data: currentKnmData } = action.payload;
    const { prosecutorOrganization } = currentKnmData;

    if (prosecutorOrganization?.ervkUuid && prosecutorOrganization?.ervkVersionUuid) {
      const ervkProsecutorOrganization: IUserOrganisation = yield fetchProsecutorOrganisationsByErvkUuidAndVersion(
        prosecutorOrganization?.ervkUuid,
        prosecutorOrganization?.ervkVersionUuid,
      );

      const userOrganizationWithDistrictSelectOption = createUserOrganizationWithDistrictSelectOption(
        ervkProsecutorOrganization,
      );

      yield put(
        decisionSetInitialFormValues({
          prosecutorOrganization: userOrganizationWithDistrictSelectOption,
        }),
      );
    } else {
      const initialProsecutorOrganization = currentKnmData?.prosecutorOrganization;
      const initialDistrictName = currentKnmData?.district?.name || null;
      const initialDistrictId = currentKnmData?.district?.id || null;

      yield put(
        decisionSetInitialFormValues({
          prosecutorOrganization:
            initialProsecutorOrganization?.id &&
            !initialProsecutorOrganization?.ervkUuid &&
            initialProsecutorOrganization?.name
              ? createKnmProsecutorOrganisationSelectOption({
                  prosecutorOrganization: {
                    ...initialProsecutorOrganization,
                    id: initialProsecutorOrganization.id,
                    name: initialProsecutorOrganization?.name,
                  },
                  districtName: initialDistrictName,
                  districtId: initialDistrictId?.toString() || null,
                })
              : undefined,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionFillAgreementProsecutorOrganizationSaga(action: ActionType<typeof decisionGetSuccess>) {
  function* funcTry() {
    yield call(createDictionaryWaitChannel);

    const state: IRootState = yield select();
    const { data: currentKnmData } = action.payload;
    const initialFormValues = getDecisionInitialFormValues(state);

    if (currentKnmData?.agreements) {
      const promises: Promise<IUserOrganisationSelectOption | undefined>[] = [];
      const organizations: IProsecutorOffice[] = [];

      // Загрузка данных о прокуратурах из справочников
      currentKnmData?.agreements?.forEach(async i => {
        // Загрузка прокуратуры из основных сведений
        if (
          i.prosecutorOrganization &&
          !organizations.find(org =>
            org.ervkUuid
              ? org.ervkUuid === i.terminate?.prosecutorOrganization?.ervkUuid &&
                org.ervkVersionUuid === i.terminate?.prosecutorOrganization?.ervkVersionUuid
              : org.id && org.id === i.terminate?.prosecutorOrganization?.id,
          )
        ) {
          promises.push(getProsecutorOrganization(i.prosecutorOrganization, currentKnmData?.district));
          organizations.push(i.prosecutorOrganization);
        }
        // Загрузка прокуратуры из сведений о расторжении
        if (
          i.terminate?.prosecutorOrganization &&
          !organizations.find(org =>
            org.ervkUuid
              ? org.ervkUuid === i.terminate?.prosecutorOrganization?.ervkUuid &&
                org.ervkVersionUuid === i.terminate?.prosecutorOrganization?.ervkVersionUuid
              : org.id && org.id === i.terminate?.prosecutorOrganization?.id,
          )
        ) {
          promises.push(getProsecutorOrganization(i.terminate?.prosecutorOrganization, currentKnmData?.district));
          organizations.push(i.terminate?.prosecutorOrganization);
        }
      });

      const results: (IUserOrganisationSelectOption | undefined)[] = yield call(() => Promise.all(promises));

      const agreementsForm = initialFormValues.agreements?.map(agreement => {
        const findOrg = (prosecutorOrganization: IUserOrganisationSelectOption | undefined) =>
          results.find(
            org =>
              org &&
              (org.ervkUuid
                ? org.ervkUuid === prosecutorOrganization?.ervkUuid &&
                  org.ervkVersionUuid === prosecutorOrganization?.ervkVersionUuid
                : org.id && org.id === prosecutorOrganization?.id),
          );

        return {
          ...agreement,
          prosecutorOrganization: findOrg(agreement.prosecutorOrganization),
          terminate: !agreement.terminate
            ? undefined
            : {
                ...agreement.terminate,
                prosecutorOrganization: findOrg(agreement.terminate?.prosecutorOrganization),
              },
        } as IErknmFormAgreement;
      });

      yield put(
        decisionSetInitialFormValues({
          agreements: agreementsForm,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionSetValueByOptionsSaga(action: ActionType<typeof dictionariesItemValueGetSuccess>) {
  const { dictionaryUUID } = action.payload;
  const state: IRootState = yield select();
  const currentData = getDecisionData(state);
  const initialFormValues = getDecisionInitialFormValues(state);

  const initialReasons = currentData?.reasons;
  const initialAct = currentData?.organization?.act;

  switch (dictionaryUUID) {
    case DictionaryUUIDsEnum.CONDITION_TYPES:
    case DictionaryUUIDsEnum.RISK_INDICATORY: {
      yield put(
        decisionSetInitialFormValues({
          reasons: convertInitialReasonsToFormDate({ reasons: initialReasons, state, initialFormValues }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.POSITIONS: {
      const organizationValue = currentData?.organization
        ? ((yield call(getOrganization, currentData?.organization, state)) as IDecisionOrganizationForm)
        : undefined;

      yield put(
        decisionSetInitialFormValues({
          organization: organizationValue,
          agreements: convertAgreementsDataToFormValues(
            currentData?.agreements,
            state,
            undefined,
            undefined,
            organizationValue,
          ),
        }),
      );

      break;
    }
    case DictionaryUUIDsEnum.PARTICIPANT_POSITIONS: {
      if (initialAct) {
        let knoInspectors: IKnmErknmFormInspector[] | undefined;

        if (initialAct?.knoInspectors?.length) {
          knoInspectors = initialAct?.knoInspectors.map(inspector => {
            const selectOption = inspector.position?.dictId
              ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
                  state,
                  inspector.position?.dictId,
                  inspector.position?.dictVersionId,
                )
              : undefined;
            const formInspector: IKnmErknmFormInspector = {
              ...inspector,
              position: selectOption,
              listId: inspector.guid || uuidv4(),
              label: `${inspector.fullName} - ${selectOption?.label}`,
            };

            return formInspector;
          });
        }

        if (initialFormValues?.organization?.act) {
          yield put(
            decisionSetInitialFormValues({
              organization: {
                ...initialFormValues?.organization,
                act: {
                  ...initialFormValues?.organization?.act,
                  knoInspectors,
                },
              },
            }),
          );
        }
      }
      break;
    }

    case DictionaryUUIDsEnum.EVENT_TYPE: {
      let events: IKnmErknmFormEvent[] | undefined;

      if (currentData?.organization?.act?.events?.length) {
        events = currentData?.organization?.act?.events
          .map(item => {
            const selectOption = item.event?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  item.event?.dictId,
                  item.event?.dictVersionId,
                )
              : undefined;
            const initFormEvent = initialFormValues.organization?.act?.events?.find(i => i.guid === item?.guid);
            let formEvent: IKnmErknmFormEvent | undefined;

            if (initFormEvent) {
              formEvent = {
                ...initFormEvent,
                type: selectOption,
              };
            }

            return formEvent;
          })
          .filter(i => !!i) as IKnmErknmFormEvent[];
      }

      if (initialFormValues.organization?.act) {
        const partialInitialFormValues: {
          organization: IDecisionOrganizationForm;
        } = {
          organization: {
            ...initialFormValues.organization,
            act: {
              ...initialFormValues.organization.act,
              events,
            },
          },
        };

        yield put(decisionSetInitialFormValues(partialInitialFormValues));
      }

      const initialEvents = currentData?.events;

      const eventsDecision = initialEvents?.map((item, index) => {
        const selectOption = item.event?.dictId
          ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
              state,
              item.event?.dictId,
              item.event?.dictVersionId,
            )
          : undefined;
        const formEvent: IKnmErknmFormEvent = {
          guid: undefined,
          startDate: undefined,
          stopDate: undefined,
          dateCreate: undefined,
          interactionCategory: undefined,
          listId: item?.guid || uuidv4(),
          ...initialFormValues.events?.[index],
          type: selectOption,
          isNew: !!item?.isNew, // false?
          isFilledFromLink: item.isFilledFromLink || undefined,
        };

        return formEvent;
      });

      const partialInitialFormValues: {
        events: IDecisionForm['events'];
      } = {
        events: eventsDecision,
      };

      yield put(decisionSetInitialFormValues(partialInitialFormValues));
      break;
    }

    default:
      break;
  }
}

function* decisionGetOrdersSaga(action: ActionType<typeof decisionGetOrders>) {
  function* funcTry() {
    const { knoId, supervisionId, conditionId, typeId } = action.payload;
    const currentConditionId: string | undefined = yield select(getDecisionAOrdersSelectConditionId);
    const currentSelectOptions: IKnmOrderSelectOption[] | undefined = yield select(getDecisionOrdersSelectOptions);

    if (currentConditionId !== conditionId || currentSelectOptions?.length === 0) {
      const result: IKnmOrder[] = yield fetchOrders(knoId, supervisionId, conditionId, false, typeId, true);

      const selectOptions: IKnmOrderSelectOption[] = result.map(createKnmOrderSelectOption);

      yield put(decisionSetOrders(selectOptions, action.payload.conditionId));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionFillOrganizationsAndObjectsByOtherPassportSaga(
  action: ActionType<typeof decisionFillOrganizationsAndObjectsByOtherPassport>,
) {
  const state: IRootState = yield select();
  const formRef = getDecisionFormRef(state);
  const currentDecisionData = getDecisionData(state);
  const currentFormData = formRef?.current.getState().values;
  const list = getDistrictSelectOptions(state);
  const currentDictionariesFromData: IKnmDictionary[] = [];
  const objects: IKnmErknmFormObject[] = [];

  if (action.payload.value.organizations.length) {
    action.payload.value.objects?.forEach(object => {
      if (object.objectType) {
        currentDictionariesFromData.push(object.objectType);
      }
      if (object.objectKind) {
        currentDictionariesFromData.push(object.objectKind);
      }
      if (object.objectSubKind) {
        currentDictionariesFromData.push(object.objectSubKind);
      }
      if (object.riskCategory) {
        currentDictionariesFromData.push(object.riskCategory);
      }
      if (object.dangerClass) {
        currentDictionariesFromData.push(object.dangerClass);
      }
      let objRegion: IKnmDistrictSelectOption | undefined;
      let objFiasRegion: IKnmDistrictSelectOption | undefined;

      const linkObjRegionId = typeof object?.region === 'string' ? object?.region : object?.region?.id?.toString();
      const linkObjFiasRegionId =
        typeof object?.fiasRegion === 'string' ? object?.fiasRegion : object?.fiasRegion?.id?.toString();

      // Смотрим Местонахождение в сведениях об объектах контроля
      if (object?.region) {
        objRegion = list.find(i => i.id.toString() === linkObjRegionId);
      }
      if (object?.fiasRegion) {
        objFiasRegion = list.find(i => i.id.toString() === linkObjFiasRegionId);
      }

      const objectTypeSelectOption = object.objectType?.dictId
        ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_TYPE>(
            state,
            object.objectType?.dictId,
            object.objectType?.dictVersionId,
          )
        : undefined;
      const objectKindSelectOption = object.objectKind?.dictId
        ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
            state,
            object.objectKind?.dictId,
            object.objectKind?.dictVersionId,
          )
        : undefined;
      const objectSubKindSelectOption = object.objectSubKind?.dictId
        ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
            state,
            object.objectSubKind?.dictId,
            object.objectSubKind?.dictVersionId,
          )
        : undefined;
      const riskCategorySelectOption = object.riskCategory?.dictId
        ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_CATEGORY>(
            state,
            object.riskCategory?.dictId,
            object.riskCategory?.dictVersionId,
          )
        : undefined;
      const dangerClassSelectOption = object.dangerClass?.dictId
        ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.DANGER_CLASS>(
            state,
            object.dangerClass?.dictId,
            object.dangerClass?.dictVersionId,
          )
        : undefined;

      objects.push({
        region: objRegion,
        fiasRegion: objFiasRegion,
        objectType: objectTypeSelectOption,
        objectKind: objectKindSelectOption,
        objectSubKind: objectSubKindSelectOption,
        riskCategory: riskCategorySelectOption,
        dangerClass: dangerClassSelectOption,
        address: object.address || undefined,
        guid: object.guid || undefined,
        ervkNumber: object.ervkNumber || undefined,
        ervkId: object.ervkId || undefined,
        cosId: object.cosId || undefined,
        cosVersion: object.cosVersion || undefined,
        name: object.name || undefined,
        streetGuid: object.streetGuid || undefined,
        houseGuid: object.houseGuid || undefined,
        roomGuid: object.roomGuid || undefined,
        steadGuid: object.steadGuid || undefined,
        addressFns: object.addressFns || undefined,
        autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : undefined,
        dateCreate: object.dateCreate || undefined,
        geolocation: object.geolocation || undefined,
        listId: object.guid || uuidv4(),
        isNew: false,
        manualAddress: object.manualAddress || undefined,
        notInFIAS: object.notInFIAS || undefined,
        regionName: object.regionName || undefined,
        isErrorFiasValue: undefined,
        inspectionResults: object.inspectionResults || undefined,
        organizationErvkId: object.organizationErvkId || undefined,
        updating: object.updating || undefined,
      });
    });

    const organization = action.payload.value.organizations[0];

    organization.act = null;
    organization.decisions = null;

    if (currentFormData) {
      const organizationFormValue = (yield call(getOrganization, organization, state)) as IDecisionOrganizationForm;
      const data = {
        ...currentFormData,
        objects,
        organization: organizationFormValue,
      };

      yield put(decisionSetInitialFormValues(data));
    }

    const dataRaw: Partial<IDecision> = {
      objects: currentDecisionData?.objects
        ? [...currentDecisionData.objects, ...action.payload.value.objects]
        : action.payload.value.objects,
    };

    yield put(decisionPatchRawData(dataRaw));

    const resultArrayActions: unknown[] = [];

    uniq(currentDictionariesFromData).forEach(item => resultArrayActions.push(getDictionariesItemValue(item)));
    yield all(resultArrayActions);
  }
}

function* decisionDownloadAgreementTemplate(action: ActionType<typeof decisionAgreementTemplateDownload>) {
  function* funcTry() {
    const { agreement } = action.payload;
    const id: ReturnType<typeof getDecisionDataId> = yield select(getDecisionDataId);

    if (id) {
      const agreementData = convertAgreementsFormValuesToData([agreement]);

      if (agreementData) {
        const response: Blob = yield fetchKnmAgreementTemplateDownload(id, agreementData[0], true);
        const file = new Blob([response]);

        saveFile(file, `${id}.docx`);
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DOWNLOAD_XML,
        }),
      );
    },
  });
}

function* decisionSendAgreementSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const decisionId = getDecisionDataId(state);

    if (decisionId) {
      const formRef = getDecisionFormRef(state);

      if (formRef?.current) {
        formRef.current.submit();

        yield put(decisionReload());
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* decisionErknmCancelPrescribeSaga(action: ActionType<typeof decisionErknmCancelPrescribe>) {
  function* funcTry() {
    const { data } = action.payload;

    yield fetchDecisionPrescribeCancel(data);
    yield put(decisionReload());
    yield put(decisionRemoveVisibleModal(KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL));
    yield put(decisionGetHistoryChanges());
    yield put(
      addNoticeAction({
        renderContent: PRESCRIBE_CANCEL_SUCCESS,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.PASSPORT_SIGN,
      }),
    );
  }
  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: PRESCRIBE_CANCEL_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PRESCRIBE_CANCEL,
        }),
      );
    },
  });
}
